/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * CustomTabContainer Component
 *
 * Acts as a wrapper for CustomTab components.
 * Handles horizontal scroll and shows static left/right navigation buttons when overflow exists.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {number|string} props.activeTab - Currently active tab id.
 * @param {Function} props.setActiveTab - Function to update active tab.
 * @param {React.ReactNode} props.children - CustomTab elements.
 * @param {string} props.className - Additional class names for the container (optional).
 * @param {string} props.classNameActions - Additional class names for the action button (optional).
 * @param {string} props.buttonAction - Additional button (optional).
 *
 * @returns {JSX.Element} Rendered CustomTabContainer.
 */

import React, {useEffect, useRef, useState} from "react";
import { BackArrowIcon, NextArrowIcon } from "./buttons/IconButtons";

const CustomTabContainer = ({ activeTab, setActiveTab, children, className,classNameActions, buttonAction }) => {
    const tabsRef = useRef(null);
    const [showScrollButtons, setShowScrollButtons] = useState(false);

    const scrollTabs = (direction) => {
        if (tabsRef.current) {
            const scrollAmount = 200;
            tabsRef.current.scrollBy({
                left: direction === "right" ? scrollAmount : -scrollAmount,
                behavior: "smooth",
            });
        }
    };

    /**
     * Checks whether the tab container content overflows horizontally
     * and determines if scroll buttons should be displayed.
     *
     * - Compares `scrollWidth` (total width of the scrollable content)
     *   with `clientWidth` (visible width of the container).
     * - If content exceeds visible width, `setShowScrollButtons(true)` is called.
     */
    const checkScrollStatus = () => {
        if (!tabsRef.current) return;
        const { scrollWidth, clientWidth } = tabsRef.current;

        setShowScrollButtons(scrollWidth > clientWidth);
    };

    /**
     * useEffect hook to monitor tab container size and scroll changes.
     *
     * - Runs once on mount.
     * - Adds listeners for `scroll` (on the tab container)
     *   and `resize` (on the window) to dynamically update scroll button visibility.
     * - Cleans up event listeners on unmount to prevent memory leaks.
     */
    useEffect(() => {
        const tabs = tabsRef.current;
        if (!tabs) return;

        checkScrollStatus();

        tabs.addEventListener("scroll", checkScrollStatus);
        window.addEventListener("resize", checkScrollStatus);

        return () => {
            tabs.removeEventListener("scroll", checkScrollStatus);
            window.removeEventListener("resize", checkScrollStatus);
        };
    }, []);

    const childrenWithProps = Array.isArray(children)
        ? children.map((child) =>
            child.type?.displayName === "CustomTab"
                ? {
                    ...child,
                    props: {
                        ...child.props,
                        isSelected: activeTab === child.props.id,
                        onClick: () => setActiveTab(child.props.id),
                    },
                }
                : child
        )
        : children;

    return (
        <div className={`d-flex align-items-center border py-2 gap-1 position-relative ${className}`} >
            {/* overlay */}
            {showScrollButtons && (
                <button
                    id="btn_ctc_scroll_tabs_left"
                    className="d-flex justify-content-center align-items-center rounded-circle border border-shades-200 bg-general-00 p-1"
                    onClick={() => scrollTabs("left")}
                >
                    <BackArrowIcon className="h-24px w-24px" />
                </button>
            )}

            <div
                ref={tabsRef}
                className="d-flex gap-1 overflow-auto flex-grow-1 p-1 border-radius-8px scroll-smooth-hidden align-items-center hide-scrollbar background-shades-100"
            >
                {childrenWithProps}
            </div>
            {buttonAction && <div className={classNameActions}>{buttonAction}</div>}
            {showScrollButtons && (
                <button
                    id="btn_ctc_scroll_tabs_right"
                    className="d-flex justify-content-center align-items-center rounded-circle border border-shades-200 bg-general-00 p-1"
                    onClick={() => scrollTabs("right")}
                >
                    <NextArrowIcon className="h-24px w-24px" />
                </button>
            )}
        </div>
    );
};

export default CustomTabContainer;
